import { ParameterError } from '../types/errors';
export const SNOWFLAKE_AI_CALLBACK_PATH = '/oauth/redirect/snowflake-ai';
export const getSnowflakeAgentRedirectUri = (siteUrl) => new URL(`/api/v1${SNOWFLAKE_AI_CALLBACK_PATH}`, siteUrl).href;
export const SNOWFLAKE_AGENT_INTEGRATION_NAME = 'LIGHTDASH_AGENT';
export const buildSnowflakeAgentIntegrationSql = ({ redirectUri, }) => `CREATE SECURITY INTEGRATION ${SNOWFLAKE_AGENT_INTEGRATION_NAME}
  TYPE = OAUTH
  OAUTH_CLIENT = CUSTOM
  OAUTH_CLIENT_TYPE = 'CONFIDENTIAL'
  OAUTH_REDIRECT_URI = '${redirectUri.replaceAll("'", "''")}'
  ENABLED = TRUE
  IS_AGENTIC = TRUE
  OAUTH_ISSUE_REFRESH_TOKENS = TRUE
  OAUTH_REFRESH_TOKEN_VALIDITY = 7776000;
SELECT SYSTEM$SHOW_OAUTH_CLIENT_SECRETS('${SNOWFLAKE_AGENT_INTEGRATION_NAME}');`;
const singleQuote = (value) => `'${value.replaceAll("'", "'\"'\"'")}'`;
const shellWord = (value) => /^[a-zA-Z0-9_./:@-]+$/.test(value) ? value : singleQuote(value);
const doubleQuote = (value) => `"${value.replace(/[\\"$`]/g, '\\$&')}"`;
const bigQueryIdentifier = (value) => `\`${value.replaceAll('\\', '\\\\').replaceAll('`', '\\`')}\``;
export const buildBigQueryAiServiceAccountCommands = ({ project, executionProject, dataset, serviceAccountName = 'lightdash-agents', }) => {
    const jobProject = executionProject ?? project;
    const member = `serviceAccount:${serviceAccountName}@${project}.iam.gserviceaccount.com`;
    const commands = [
        {
            step: 'create',
            title: 'Create the service account',
            command: `gcloud iam service-accounts create ${shellWord(serviceAccountName)} \\
  --project=${shellWord(project)} \\
  --display-name="Silicon agents"`,
        },
        {
            step: 'job_user',
            title: 'Allow the service account to run jobs',
            command: `gcloud projects add-iam-policy-binding ${shellWord(jobProject)} \\
  --member=${doubleQuote(member)} \\
  --role="roles/bigquery.jobUser"`,
        },
    ];
    if (dataset) {
        const schema = /^[a-zA-Z_][a-zA-Z0-9_]*$/.test(dataset)
            ? dataset
            : bigQueryIdentifier(dataset);
        const sqlMember = member
            .replaceAll('\\', '\\\\')
            .replaceAll('"', '\\"');
        const grant = `GRANT \`roles/bigquery.dataViewer\` ON SCHEMA ${bigQueryIdentifier(project)}.${schema} TO "${sqlMember}"`;
        commands.push({
            step: 'data_viewer',
            title: 'Allow the service account to read the dataset',
            command: `bq query \\
  --project_id=${shellWord(jobProject)} \\
  --nouse_legacy_sql \\
  ${singleQuote(grant)}`,
        });
    }
    return commands;
};
export const parseSnowflakeAccountUrl = (input) => {
    const value = input.trim();
    const invalid = () => new ParameterError('Provide an HTTPS Snowflake account URL with no path, credentials, query or fragment.');
    if (!value || /[\\\s?#@]/.test(value))
        throw invalid();
    const candidate = value.includes('://') ? value : `https://${value}`;
    if (!/^https:\/\/[^/]+\/?$/i.test(candidate))
        throw invalid();
    let url;
    try {
        url = new URL(candidate);
    }
    catch {
        throw invalid();
    }
    const suffix = '.snowflakecomputing.com';
    if (url.protocol !== 'https:' ||
        url.username ||
        url.password ||
        url.port ||
        url.pathname !== '/' ||
        !url.hostname.endsWith(suffix))
        throw invalid();
    const accountHost = url.hostname.slice(0, -suffix.length);
    if (!accountHost ||
        !accountHost
            .split('.')
            .every((label) => /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/.test(label)))
        throw invalid();
    const accountIdentifier = accountHost
        .split('.')
        .filter((label) => label !== 'privatelink')
        .join('.');
    if (!accountIdentifier)
        throw invalid();
    return {
        accountUrl: url.origin,
        accountIdentifier,
        authorizationEndpoint: `${url.origin}/oauth/authorize`,
        tokenEndpoint: `${url.origin}/oauth/token-request`,
    };
};
export const buildDatabricksAiServiceAccountCommands = ({ catalog, schema, }) => {
    const quote = (value) => `\`${value.replaceAll('`', '``')}\``;
    const catalogIdentifier = quote(catalog || '<catalog>');
    const schemaIdentifier = `${catalogIdentifier}.${quote(schema || '<schema>')}`;
    const principal = quote('<service-principal-application-id>');
    return `GRANT USE CATALOG ON CATALOG ${catalogIdentifier} TO ${principal};
GRANT USE SCHEMA ON SCHEMA ${schemaIdentifier} TO ${principal};
GRANT SELECT ON TABLE ${schemaIdentifier}.${quote('<table>')} TO ${principal};`;
};
export const buildAthenaAiServiceAccountCommands = ({ region, catalog, database, }) => {
    const awsRegion = region || '<region>';
    const glueDatabase = database || '<glue-database>';
    const athenaArn = `arn:<partition>:athena:${awsRegion}:<account-id>`;
    const glueArn = `arn:<partition>:glue:${awsRegion}:<account-id>`;
    const roleTrustPolicy = {
        Version: '2012-10-17',
        Statement: [
            {
                Effect: 'Allow',
                Principal: { AWS: '<trusted-caller-iam-arn>' },
                Action: 'sts:AssumeRole',
            },
        ],
    };
    const permissionPolicy = {
        Version: '2012-10-17',
        Statement: [
            {
                Effect: 'Allow',
                Action: [
                    'athena:StartQueryExecution',
                    'athena:GetQueryExecution',
                    'athena:GetQueryResults',
                    'athena:GetWorkGroup',
                ],
                Resource: `${athenaArn}:workgroup/<agent-workgroup>`,
            },
            {
                Effect: 'Allow',
                Action: [
                    'athena:GetDataCatalog',
                    'athena:GetDatabase',
                    'athena:GetTableMetadata',
                    'athena:ListDatabases',
                    'athena:ListTableMetadata',
                ],
                Resource: `${athenaArn}:datacatalog/${catalog || '<catalog>'}`,
            },
            {
                Effect: 'Allow',
                Action: [
                    's3:GetBucketLocation',
                    's3:ListBucket',
                    's3:ListBucketMultipartUploads',
                ],
                Resource: 'arn:<partition>:s3:::<agent-results-bucket>',
            },
            {
                Effect: 'Allow',
                Action: [
                    's3:GetObject',
                    's3:PutObject',
                    's3:AbortMultipartUpload',
                    's3:ListMultipartUploadParts',
                ],
                Resource: 'arn:<partition>:s3:::<agent-results-bucket>/<agent-results-prefix>/*',
            },
            {
                Effect: 'Allow',
                Action: [
                    'glue:GetDatabase',
                    'glue:GetDatabases',
                    'glue:GetTable',
                    'glue:GetTables',
                    'glue:GetPartition',
                    'glue:GetPartitions',
                    'glue:BatchGetPartition',
                ],
                Resource: [
                    `${glueArn}:catalog`,
                    `${glueArn}:database/${glueDatabase}`,
                    `${glueArn}:table/${glueDatabase}/*`,
                ],
            },
            {
                Effect: 'Allow',
                Action: 'lakeformation:GetDataAccess',
                Resource: '*',
            },
        ],
    };
    const principal = JSON.stringify({
        DataLakePrincipalIdentifier: '<agent-iam-principal-arn>',
    });
    const grant = (resource, permission) => `aws lakeformation grant-permissions --region ${shellWord(awsRegion)} \\
  --principal ${singleQuote(principal)} \\
  --resource ${singleQuote(JSON.stringify(resource))} \\
  --permissions ${permission}`;
    return {
        roleTrustPolicy: JSON.stringify(roleTrustPolicy, null, 2),
        permissionPolicy: JSON.stringify(permissionPolicy, null, 2),
        lakeFormationGrants: [
            grant({ Database: { CatalogId: '<account-id>', Name: glueDatabase } }, 'DESCRIBE'),
            grant({
                Table: {
                    CatalogId: '<account-id>',
                    DatabaseName: glueDatabase,
                    Name: '<table>',
                },
            }, 'SELECT'),
        ].join('\n'),
        filteredTableGrant: grant({
            DataCellsFilter: {
                TableCatalogId: '<account-id>',
                DatabaseName: glueDatabase,
                TableName: '<table>',
                Name: '<data-filter>',
            },
        }, 'SELECT'),
    };
};
export const buildPostgresAiServiceAccountCommands = ({ dbname, schema, }) => {
    const quoteIdentifier = (value) => `"${value.replaceAll('"', '""')}"`;
    const databaseIdentifier = quoteIdentifier(dbname || '<database>');
    const schemaIdentifier = quoteIdentifier(schema || '<schema>');
    const role = quoteIdentifier('ai_agents');
    const table = `${schemaIdentifier}.${quoteIdentifier('<table>')}`;
    return {
        createRole: `CREATE ROLE ${role} LOGIN PASSWORD '<choose-a-strong-password>'
  NOSUPERUSER NOCREATEDB NOCREATEROLE NOBYPASSRLS;
ALTER ROLE ${role} SET default_transaction_read_only = on;
GRANT CONNECT ON DATABASE ${databaseIdentifier} TO ${role};`,
        grantReadAccess: `GRANT USAGE ON SCHEMA ${schemaIdentifier} TO ${role};
GRANT SELECT ON ALL TABLES IN SCHEMA ${schemaIdentifier} TO ${role};
ALTER DEFAULT PRIVILEGES IN SCHEMA ${schemaIdentifier} GRANT SELECT ON TABLES TO ${role};`,
        rowLevelSecurity: `ALTER TABLE ${table} ENABLE ROW LEVEL SECURITY;
CREATE POLICY "ai_agents_rows" ON ${table}
  FOR SELECT TO ${role} USING (<condition>);`,
    };
};
export const buildRedshiftAiServiceAccountCommands = ({ schema, }) => {
    const quoteIdentifier = (value) => `"${value.replaceAll('"', '""')}"`;
    const schemaIdentifier = quoteIdentifier(schema || '<schema>');
    const table = `${schemaIdentifier}."<table>"`;
    return {
        createUser: `CREATE USER ai_agents
  PASSWORD '<choose-a-strong-password>'
  NOCREATEDB NOCREATEUSER
  SYSLOG ACCESS RESTRICTED;`,
        grantReadAccess: `GRANT USAGE ON SCHEMA ${schemaIdentifier}
  TO ai_agents;
GRANT SELECT ON ALL TABLES
  IN SCHEMA ${schemaIdentifier}
  TO ai_agents;
ALTER DEFAULT PRIVILEGES
  FOR USER "<table-owner>"
  IN SCHEMA ${schemaIdentifier}
  GRANT SELECT ON TABLES TO ai_agents;`,
        rowLevelSecurity: `CREATE RLS POLICY ai_agents_rows
  WITH ("<column>" <type>)
  USING (<condition>);
ATTACH RLS POLICY ai_agents_rows
  ON ${table}
  TO ai_agents;
ALTER TABLE ${table}
  ROW LEVEL SECURITY ON;`,
        masking: `CREATE MASKING POLICY ai_agents_mask
  WITH (value VARCHAR(256))
  USING ('[redacted]'::VARCHAR(256));
ATTACH MASKING POLICY ai_agents_mask
  ON ${table} ("<column>")
  TO ai_agents;`,
    };
};
export const buildTrinoAiServiceAccountCommands = ({ dbname, schema, user = '<user>', table = '<table>', }) => {
    const regexLiteral = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const quoteIdentifier = (value) => `"${value.replaceAll('"', '""')}"`;
    const catalogName = dbname || '<catalog>';
    const schemaName = schema || '<schema>';
    const userPattern = regexLiteral(user);
    const catalogPattern = regexLiteral(catalogName);
    const rules = {
        catalogs: [
            { user: userPattern, catalog: catalogPattern, allow: 'read-only' },
            { user: userPattern, allow: 'none' },
        ],
        schemas: [
            {
                user: userPattern,
                catalog: catalogPattern,
                schema: regexLiteral(schemaName),
                owner: false,
            },
            { user: userPattern, owner: false },
        ],
        tables: [
            {
                user: userPattern,
                catalog: catalogPattern,
                schema: regexLiteral(schemaName),
                table: regexLiteral(table),
                privileges: ['SELECT'],
                filter: '<row-filter-expression>',
                columns: [{ name: '<column>', mask: '<mask-expression>' }],
            },
            { user: userPattern, privileges: [] },
        ],
    };
    return {
        accessControlRules: JSON.stringify(rules, null, 2),
        grantReadAccess: `CREATE ROLE ai_agents_read;
GRANT SELECT ON TABLE ${quoteIdentifier(catalogName)}.${quoteIdentifier(schemaName)}.${quoteIdentifier(table)}
  TO ROLE ai_agents_read;
GRANT ai_agents_read TO USER ${quoteIdentifier(user)};`,
    };
};
export const buildClickhouseAiServiceAccountCommands = ({ schema, }) => {
    const quoteIdentifier = (value) => `\`${value
        .replaceAll('\\', '\\\\')
        .replaceAll('`', '\\`')
        .replaceAll('\n', '\\n')
        .replaceAll('\r', '\\r')
        .replaceAll('\t', '\\t')}\``;
    const database = quoteIdentifier(schema || '<database>');
    return {
        createUser: `CREATE USER ai_agents
  IDENTIFIED WITH sha256_password
  BY '<choose-a-strong-password>'
  SETTINGS readonly = 2;`,
        grantReadAccess: `GRANT SELECT ON ${database}.*
  TO ai_agents;`,
        rowPolicy: `CREATE ROW POLICY ai_agents_rows
  ON ${database}.\`<table>\`
  FOR SELECT USING <condition>
  TO ai_agents;`,
    };
};
//# sourceMappingURL=agentIdentitySetup.js.map